import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Legend,
  Tooltip,
} from 'recharts';
import type { CompletionStats } from '../../types';

interface CompletionChartProps {
  stats: CompletionStats;
  title?: string;
}

const COLORS = {
  completed: '#70B77E',
  failed: '#F0544F',
  cancelled: '#FEFFD3',
};

interface ChartDataItem {
  name: string;
  value: number;
  color: string;
  percent: number;
}

export function CompletionChart({ stats, title = 'Completion Rate' }: CompletionChartProps) {
  // Pie chart percentages are relative to total (for correct visual proportions)
  // Success rate (completedPercent) excludes cancelled scans
  const data: ChartDataItem[] = [
    { name: 'Completed', value: stats.completed, color: COLORS.completed, percent: stats.total > 0 ? (stats.completed / stats.total) * 100 : 0 },
    { name: 'Failed', value: stats.failed, color: COLORS.failed, percent: stats.total > 0 ? (stats.failed / stats.total) * 100 : 0 },
    { name: 'Cancelled', value: stats.cancelled, color: COLORS.cancelled, percent: stats.cancelledPercent },
  ].filter(d => d.value > 0);

  const hasData = stats.total > 0;

  return (
    <div className="bg-white rounded-lg border border-surface-200 p-3 shadow-card h-full flex flex-col">
      <div className="flex items-center gap-2 mb-2 flex-shrink-0">
        <div className="w-7 h-7 rounded bg-blue-100 flex items-center justify-center">
          <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h2 className="text-sm font-semibold text-surface-800">{title}</h2>
        <div className="ml-auto flex items-center gap-3">
          <div className="text-center">
            <p className="text-lg font-bold text-[#70B77E]">{stats.completedPercent.toFixed(1)}%</p>
            <p className="text-[10px] text-surface-500 uppercase">Success</p>
          </div>
          <div className="h-6 w-px bg-surface-200" />
          <div className="text-center">
            <p className="text-lg font-bold text-surface-700">{stats.total}</p>
            <p className="text-[10px] text-surface-500 uppercase">Total</p>
          </div>
        </div>
      </div>

      {/* Pie Chart */}
      {hasData ? (
        <div className="flex-1 min-h-0">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={data}
                cx="50%"
                cy="50%"
                innerRadius="35%"
                outerRadius="60%"
                paddingAngle={2}
                dataKey="value"
                label={({ name, percent }) => `${name}: ${percent.toFixed(1)}%`}
                labelLine={{ stroke: '#64748b', strokeWidth: 1 }}
              >
                {data.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{
                  backgroundColor: '#fff',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                }}
                formatter={(value: number, name: string) => [`${value} scans`, name]}
              />
              <Legend
                verticalAlign="bottom"
                height={24}
                formatter={(value: string) => <span className="text-surface-600 text-xs">{value}</span>}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="flex-1 min-h-0 flex items-center justify-center text-surface-400">
          No scan data available
        </div>
      )}

      {/* Status breakdown */}
      <div className="grid grid-cols-3 gap-2 mt-2 pt-2 border-t border-surface-100 flex-shrink-0">
        <div className="text-center">
          <div className="flex items-center justify-center gap-1">
            <div className="w-2 h-2 rounded-full bg-[#70B77E]" />
            <span className="text-xs font-medium text-surface-700">{stats.completed}</span>
          </div>
          <p className="text-[10px] text-surface-500">Completed</p>
        </div>
        <div className="text-center">
          <div className="flex items-center justify-center gap-1">
            <div className="w-2 h-2 rounded-full bg-[#F0544F]" />
            <span className="text-xs font-medium text-surface-700">{stats.failed}</span>
          </div>
          <p className="text-[10px] text-surface-500">Failed</p>
        </div>
        <div className="text-center">
          <div className="flex items-center justify-center gap-1">
            <div className="w-2 h-2 rounded-full bg-surface-400" />
            <span className="text-xs font-medium text-surface-700">{stats.cancelled}</span>
          </div>
          <p className="text-[10px] text-surface-500">Cancelled</p>
        </div>
      </div>
    </div>
  );
}
